<template>
  <div class="mod-page list-full">
    <div class="mod-header">
      <div>
        <h1 class="mod-title">Personas Contacto</h1>
        <p class="mod-subtitle">Contactos externos por empresa, con mapeo ERP</p>
      </div>
      <div class="mod-header-actions">
        <input ref="fileInput" type="file" accept=".xlsx,.xls,.csv" class="d-none" @change="onImportFile">
        <button class="mod-btn mod-btn-outline" @click="downloadTemplate">
          <i class="bi bi-file-earmark-arrow-down"></i>
          <span>Plantilla</span>
        </button>
        <button class="mod-btn mod-btn-outline" @click="$refs.fileInput.click()" :disabled="importing">
          <span v-if="importing" class="mod-spinner"></span>
          <i v-else class="bi bi-upload"></i>
          <span>{{ importing ? 'Importando...' : 'Importar' }}</span>
        </button>
        <button class="mod-btn mod-btn-outline" @click="exportExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span>Exportar</span>
        </button>
        <button class="mod-btn mod-btn-outline" @click="sincronizarErp" :disabled="syncing">
          <span v-if="syncing" class="mod-spinner"></span>
          <i v-else class="bi bi-arrow-repeat"></i>
          <span>{{ syncing ? 'Sincronizando...' : 'Sincronizar ERP' }}</span>
        </button>
        <button class="mod-btn mod-btn-primary" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nueva persona</span>
        </button>
      </div>
    </div>

    <!-- Controles de búsqueda y filtros -->
    <div class="list-controls">
      <div class="mod-search list-search">
        <i class="bi bi-search"></i>
        <input
          v-model="searchText"
          type="text"
          placeholder="Buscar por clave, nombre, empresa, email, teléfono..."
          @input="applyFilters"
        >
        <button v-if="searchText" class="list-clear-btn" @click="searchText = ''; applyFilters()">
          <i class="bi bi-x"></i>
        </button>
      </div>
      <select v-model="typeFilter" class="mod-select" @change="applyFilters">
        <option value="">Tipo: Todos</option>
        <option v-for="tipo in tiposDisponibles" :key="tipo" :value="tipo">{{ tipo }}</option>
      </select>
      <select v-model="filterActivo" class="mod-select" @change="applyFilters">
        <option value="">Activo: Todos</option>
        <option value="1">Solo activos</option>
        <option value="0">Solo inactivos</option>
      </select>
      <select v-model="pageSize" class="mod-select" @change="applyFilters">
        <option :value="10">10 por página</option>
        <option :value="25">25 por página</option>
        <option :value="50">50 por página</option>
        <option :value="100">100 por página</option>
      </select>
      <span class="mod-filter-count">{{ totalFiltered }} registro{{ totalFiltered !== 1 ? 's' : '' }}</span>
    </div>

    <!-- Tabla -->
    <div class="mod-table-wrap">
      <table class="mod-table">
        <thead>
          <tr>
            <th class="mod-th-sort" @click="sortBy('Clave')">Clave <i :class="sortIcon('Clave')"></i></th>
            <th class="mod-th-sort" @click="sortBy('Nombre')">Nombre <i :class="sortIcon('Nombre')"></i></th>
            <th class="mod-th-sort" @click="sortBy('TipoPersonaContacto')">Tipo <i :class="sortIcon('TipoPersonaContacto')"></i></th>
            <th class="mod-th-sort" @click="sortBy('EmpresaNombre')">Empresa <i :class="sortIcon('EmpresaNombre')"></i></th>
            <th class="mod-th-sort" @click="sortBy('Cargo')">Cargo <i :class="sortIcon('Cargo')"></i></th>
            <th>Contacto</th>
            <th class="mod-th-sort" @click="sortBy('ClaveErp')">Clave ERP <i :class="sortIcon('ClaveErp')"></i></th>
            <th class="mod-th-sort" @click="sortBy('EsActivo')">Estado <i :class="sortIcon('EsActivo')"></i></th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="pagedItems.length === 0">
            <td colspan="9" class="mod-td-center">
              <div class="mod-td-empty">
                <i class="bi bi-person-lines-fill"></i>
                <span>{{ hayFiltros ? 'Sin resultados para los filtros aplicados' : 'No hay personas contacto registradas' }}</span>
                <button v-if="hayFiltros" class="list-clear-filters" @click="clearFilters">
                  Limpiar filtros
                </button>
              </div>
            </td>
          </tr>
          <tr v-for="item in pagedItems" :key="item.Clave + '-' + item.EmpresaId" class="mod-tr">
            <td><span class="mod-badge">{{ item.Clave }}</span></td>
            <td>
              <div class="list-nombre-main">{{ nombreCompleto(item) }}</div>
              <div v-if="item.SocioNegocioClave" class="list-nombre-sub">
                <i class="bi bi-link-45deg"></i> {{ item.SocioNegocioClave }}
              </div>
            </td>
            <td>
              <span v-if="item.TipoPersonaContacto" class="list-tipo-tag">{{ item.TipoPersonaContacto }}</span>
              <span v-else class="mod-td-muted">—</span>
            </td>
            <td>{{ item.EmpresaNombre || '—' }}</td>
            <td class="mod-td-muted">{{ item.Cargo || '—' }}</td>
            <td>
              <div v-if="item.Email">{{ item.Email }}</div>
              <div v-if="item.Celular || item.Telefono" class="list-nombre-sub">
                <i class="bi bi-telephone"></i> {{ item.Celular || item.Telefono }}
              </div>
              <span v-if="!item.Email && !item.Celular && !item.Telefono" class="mod-td-muted">—</span>
            </td>
            <td class="mod-td-muted">{{ item.ClaveErp || '—' }}</td>
            <td>
              <span :class="item.EsActivo ? 'mod-status-active' : 'mod-status-inactive'">
                {{ item.EsActivo ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
            <td class="mod-td-actions">
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginacion -->
    <div class="mod-pagination" v-if="totalPages > 1">
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
        <i class="bi bi-chevron-double-left"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="bi bi-chevron-left"></i>
      </button>
      <template v-for="p in visiblePages" :key="p">
        <button class="mod-page-btn" :class="{ 'mod-page-active': p === currentPage }" @click="currentPage = p">{{ p }}</button>
      </template>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
        <i class="bi bi-chevron-double-right"></i>
      </button>
      <span class="mod-page-info">Página {{ currentPage }} de {{ totalPages }}</span>
    </div>

    <PersonaContactoForm
      v-if="showForm"
      :item="selectedItem"
      :is-edit="isEdit"
      @saved="onSaved"
      @close="showForm = false"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import personaContactoService from './personacontacto.service';
import authService from '@/services/auth';
import { showToast } from '@/shared/utils/toast';
import PersonaContactoForm from './PersonaContactoForm.vue';

const COLUMNS = [
  'Clave', 'Nombre', 'Apellidos', 'TipoPersonaContacto', 'EmpresaNombre',
  'SocioNegocioClave', 'Cargo', 'Departamento', 'Email', 'EmailAlterno',
  'Telefono', 'Celular', 'Direccion', 'Ciudad', 'Estado', 'PaisClave',
  'CodigoPostal', 'ClaveErp', 'OrigenErp', 'Notas', 'EsActivo'
];

export default {
  name: 'PersonaContactoList',
  components: { PersonaContactoForm },
  data() {
    return {
      items: [],
      filteredItems: [],
      searchText: '',
      typeFilter: '',
      filterActivo: '',
      loading: false,
      syncing: false,
      importing: false,
      showForm: false,
      selectedItem: null,
      isEdit: false,
      currentPage: 1,
      pageSize: 25,
      sortField: 'Nombre',
      sortAsc: true
    };
  },
  computed: {
    hayFiltros() {
      return !!(this.searchText || this.typeFilter || this.filterActivo);
    },
    totalFiltered() { return this.filteredItems.length; },
    totalPages() { return Math.ceil(this.totalFiltered / this.pageSize) || 1; },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4);
        else start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    tiposDisponibles() {
      return [...new Set(this.items.map(i => i.TipoPersonaContacto).filter(Boolean))].sort();
    }
  },
  watch: {
    currentPage(val) { if (val > this.totalPages) this.currentPage = this.totalPages; }
  },
  created() {
    this.loadItems();
  },
  methods: {
    async loadItems() {
      this.loading = true;
      try {
        const empresa = authService.getSelectedEmpresa();
        const result = await personaContactoService.listar({ EmpresaId: empresa?.EmpresaId || 0 });
        this.items = Array.isArray(result) ? result : (result.OData || []);
        this.applyFilters();
      } catch {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      let data = [...this.items];

      if (this.typeFilter) {
        data = data.filter(i => i.TipoPersonaContacto === this.typeFilter);
      }

      if (this.filterActivo !== '') {
        const activo = this.filterActivo === '1';
        data = data.filter(i => !!i.EsActivo === activo);
      }

      const q = this.searchText.toLowerCase().trim();
      if (q) {
        data = data.filter(i =>
          (i.Clave || '').toLowerCase().includes(q) ||
          this.nombreCompleto(i).toLowerCase().includes(q) ||
          (i.TipoPersonaContacto || '').toLowerCase().includes(q) ||
          (i.EmpresaNombre || '').toLowerCase().includes(q) ||
          (i.SocioNegocioClave || '').toLowerCase().includes(q) ||
          (i.Cargo || '').toLowerCase().includes(q) ||
          (i.Email || '').toLowerCase().includes(q) ||
          (i.Telefono || '').toLowerCase().includes(q) ||
          (i.Celular || '').toLowerCase().includes(q) ||
          (i.ClaveErp || '').toLowerCase().includes(q)
        );
      }

      data.sort((a, b) => {
        let va = a[this.sortField] ?? '';
        let vb = b[this.sortField] ?? '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });

      this.filteredItems = data;
      this.currentPage = 1;
    },
    clearFilters() {
      this.searchText = '';
      this.typeFilter = '';
      this.filterActivo = '';
      this.applyFilters();
    },
    sortBy(field) {
      if (this.sortField === field) this.sortAsc = !this.sortAsc;
      else { this.sortField = field; this.sortAsc = true; }
      this.applyFilters();
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'bi bi-chevron-expand mod-sort-inactive';
      return this.sortAsc ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    nombreCompleto(item) {
      return [item.Nombre, item.Apellidos].filter(Boolean).join(' ').trim();
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    onSaved() {
      this.showForm = false;
      this.loadItems();
    },
    async confirmDelete(item) {
      if (!confirm(`Eliminar persona contacto "${this.nombreCompleto(item)}"?`)) return;
      try {
        const result = await personaContactoService.borrar(item);
        if (result.Number >= 0) {
          showToast(result.Message || 'Eliminado correctamente', result.Type || 'success');
          this.loadItems();
        } else {
          alert(result.Message || 'Error al eliminar');
        }
      } catch {
        alert('Error al eliminar');
      }
    },
    async sincronizarErp() {
      this.syncing = true;
      try {
        const result = await personaContactoService.sincronizarErp();
        if (result.Number >= 0) {
          showToast(result.Message || 'Sincronización completada', result.Type || 'success');
          this.loadItems();
        } else {
          showToast(result.Message || 'Error en sincronización', 'error');
        }
      } catch {
        showToast('Error de conexión al sincronizar', 'error');
      } finally {
        this.syncing = false;
      }
    },
    exportExcel() {
      this.writeWorkbook(this.filteredItems, 'PersonasContacto', 'personas_contacto.xlsx');
    },
    downloadTemplate() {
      const sample = [{
        Clave: 'CONT-001', Nombre: 'Nombre', Apellidos: 'Apellidos',
        TipoPersonaContacto: 'Contratista', EmpresaNombre: 'Empresa externa',
        SocioNegocioClave: '', Cargo: 'Responsable técnico', Departamento: '',
        Email: 'contacto@empresa.com', EmailAlterno: '', Telefono: '', Celular: '',
        Direccion: '', Ciudad: '', Estado: '', PaisClave: 'MX', CodigoPostal: '',
        ClaveErp: '', OrigenErp: '', Notas: '', EsActivo: 'Si'
      }];
      this.writeWorkbook(sample, 'Plantilla', 'plantilla_personas_contacto.xlsx');
    },
    writeWorkbook(rows, sheetName, fileName) {
      const data = rows.map(i => {
        const row = {};
        COLUMNS.forEach(col => {
          if (col === 'EsActivo') row[col] = i[col] === true || i[col] === 'Si' ? 'Si' : 'No';
          else row[col] = i[col] ?? '';
        });
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = COLUMNS.map(k => ({
        wch: Math.max(k.length, ...data.map(r => String(r[k] || '').length)) + 2
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), fileName);
    },
    async onImportFile(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      this.importing = true;
      try {
        const rows = await this.readWorkbook(file);
        const empresa = authService.getSelectedEmpresa();
        const payload = rows
          .map(row => this.normalizeImportRow(row, empresa?.EmpresaId || 0))
          .filter(row => row.Clave && row.Nombre);
        if (payload.length === 0) {
          showToast('El archivo no contiene filas válidas', 'warning');
          return;
        }
        const result = await personaContactoService.importarMasivo(payload);
        if (result.Number >= 0) {
          showToast(result.Message || 'Importación completada', result.Type || 'success');
          await this.loadItems();
        } else {
          showToast(result.Message || 'Error al importar', 'error');
        }
      } catch {
        showToast('No fue posible importar el archivo', 'error');
      } finally {
        this.importing = false;
      }
    },
    readWorkbook(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => {
          try {
            const data = new Uint8Array(e.target.result);
            const wb = XLSX.read(data, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            resolve(XLSX.utils.sheet_to_json(ws, { defval: '' }));
          } catch (err) { reject(err); }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },
    normalizeImportRow(row, empresaId) {
      const item = {};
      COLUMNS.forEach(col => { item[col] = row[col] ?? ''; });
      item.EmpresaId = empresaId;
      item.EsActivo = this.parseBool(item.EsActivo);
      return item;
    },
    parseBool(value) {
      if (typeof value === 'boolean') return value;
      const v = String(value ?? '').trim().toLowerCase();
      if (!v) return true;
      return ['si', 's', 'true', '1', 'activo', 'yes'].includes(v);
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

/* ── Ancho completo ─────────────────────────────────── */
.mod-page.list-full {
  max-width: none;
  padding: 1.5rem 2rem;
}

/* ── Barra de controles ─────────────────────────────── */
.list-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.list-search {
  flex: 1;
  min-width: 200px;
  max-width: 480px;
}

.list-clear-btn {
  background: none;
  border: none;
  padding: 0.15rem 0.3rem;
  color: #a0aec0;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  border-radius: 4px;
  transition: color 0.15s;
}

.list-clear-btn:hover { color: #4a5568; }

/* ── Celdas de nombre ───────────────────────────────── */
.list-nombre-main {
  font-weight: 500;
  color: #1a202c;
  line-height: 1.3;
}

.list-nombre-sub {
  font-size: 0.78rem;
  color: #a0aec0;
  margin-top: 0.2rem;
  line-height: 1.3;
}

.list-nombre-sub i {
  font-size: 0.7rem;
}

/* ── Badge de tipo ──────────────────────────────────── */
.list-tipo-tag {
  display: inline-block;
  padding: 0.15rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #edf2f7;
  color: #4a5568;
  white-space: nowrap;
}

/* ── Vacío con limpiar filtros ──────────────────────── */
.list-clear-filters {
  margin-top: 0.5rem;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.35rem 0.85rem;
  font-size: 0.8rem;
  color: var(--t-primary, #1272c4);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.list-clear-filters:hover {
  background: #ebf4ff;
  border-color: var(--t-primary, #1272c4);
}

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 768px) {
  .mod-page.list-full { padding: 1rem 0.75rem; }
  .list-controls { flex-direction: column; align-items: stretch; }
  .list-search { max-width: none; }
}
</style>
